import { Button, Flex, Loader, Text } from "@mantine/core";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isSessionAlreadyEnded, ory, redirectToOryLogin } from "utils/ory";

type VerificationState = "verifying" | "signedOut" | "failed";

export const LoggedOut = () => {
  const { t } = useTranslation("common");
  const [state, setState] = useState<VerificationState>("verifying");

  useEffect(() => {
    const verifyLoggedOut = async () => {
      try {
        await ory.toSession();
      } catch (error) {
        if (isSessionAlreadyEnded(error)) {
          setState("signedOut");
          return;
        }

        Sentry.captureException(error, {
          tags: { section: "auth", step: "loggedOut" },
        });

        setState("failed");
        return;
      }

      Sentry.captureMessage("Session still active after logout", {
        level: "warning",
        tags: { section: "auth", step: "loggedOut" },
      });

      setState("failed");
    };

    verifyLoggedOut();
  }, []);

  return (
    <Flex
      direction={"column"}
      justify={"center"}
      align={"center"}
      gap={"md"}
      className="w-screen h-screen bg-white"
    >
      <Text className="text-gray-dark text-xl font-bold tracking-wide">
        JAPP
      </Text>

      {state === "verifying" && <Loader data-testid="loader" />}

      {state === "failed" && (
        <Text data-testid="logout-failed" className="text-red-error">
          {t("logoutFailed")}
        </Text>
      )}

      {state === "signedOut" && (
        <>
          <Text data-testid="logged-out" className="text-gray-dark text-xl">
            {t("loggedOutTitle")}
          </Text>
          <Button
            data-testid="login-again"
            color={"gray.8"}
            size={"md"}
            onClick={redirectToOryLogin}
          >
            {t("loginAgain")}
          </Button>
        </>
      )}
    </Flex>
  );
};
